import React, {useEffect, useRef, useState} from 'react';
import classNames from 'classnames';
import {ChevronLeftIcon, ChevronRightIcon} from 'components';
import ReactDOM from 'react-dom';

export interface HandleProps {
  prefixCls?: string;
  className?: string;
  vertical?: boolean;
  reverse?: boolean;
  offset?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
  min?: number;
  max?: number;
  value?: number;
  tabIndex?: number;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaValueTextFormatter?: (val: number) => string;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
}

const useMouseUpListener = (handleMouseUp: () => void) => {
  useEffect(() => {
    const listener = addEventListener(document, 'mouseup', handleMouseUp);

    return () => {
      listener.remove();
    };
  }, [handleMouseUp]);
};

const getPositionStyle = (
  vertical: boolean | undefined,
  reverse: boolean | undefined,
  offset: number | undefined,
) => {
  if (vertical) {
    return {
      [reverse ? 'top' : 'bottom']: `${offset}%`,
      [reverse ? 'bottom' : 'top']: 'auto',
      transform: reverse ? undefined : `translateY(+50%)`,
    };
  }

  return {
    [reverse ? 'right' : 'left']: `${offset}%`,
    [reverse ? 'left' : 'right']: 'auto',
    transform: `translateX(${reverse ? '+' : '-'}50%)`,
  };
};

const getMergedTabIndex = (
  tabIndex: number | undefined,
  disabled: boolean | undefined,
) => {
  if (disabled || tabIndex === null) {
    return undefined;
  }
  return tabIndex || 0;
};

const getAriaValueText = (
  ariaValueTextFormatter: ((val: number) => string) | undefined,
  value: number | undefined,
) => {
  if (ariaValueTextFormatter && value !== undefined) {
    return ariaValueTextFormatter(value);
  }
  return undefined;
};

export const Handle: React.FC<HandleProps> = props => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    prefixCls: _prefixCls,
    vertical,
    reverse,
    offset,
    style,
    disabled,
    min,
    max,
    value,
    tabIndex,
    ariaLabel,
    ariaLabelledBy,
    ariaValueTextFormatter,
    ...restProps
  } = props;

  const [_clickFocused, setClickFocused] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);

  useMouseUpListener(() => {
    if (document.activeElement === handleRef.current) {
      setClickFocused(true);
    }
  });

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    handleRef.current?.focus();
  };

  const className = classNames(
    props.className,
    '-mt-2 w-6 h-6 rounded-full bg-blue-700 flex flex-row items-center justify-center text-white',
    'border-0',
  );

  const elStyle = {
    ...style,
    ...getPositionStyle(vertical, reverse, offset),
  };

  const mergedTabIndex = getMergedTabIndex(tabIndex, disabled);
  const ariaValueText = getAriaValueText(ariaValueTextFormatter, value);

  return (
    <div
      ref={handleRef}
      tabIndex={mergedTabIndex}
      {...restProps}
      className={className}
      style={elStyle}
      onBlur={() => setClickFocused(false)}
      onKeyDown={() => setClickFocused(false)}
      onMouseDown={handleMouseDown}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-disabled={!!disabled}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-valuetext={ariaValueText}
    >
      <ChevronLeftIcon strokeColor="text-white" width={10} height={10} />
      <ChevronRightIcon strokeColor="text-white" width={10} height={10} />
    </div>
  );
};

function addEventListener(
  target: EventTarget | null,
  eventType: string,
  cb: (event: Event) => void,
  option?: boolean | AddEventListenerOptions,
): {remove: () => void} {
  const callback = (ReactDOM as any).unstable_batchedUpdates
    ? (e: Event) => {
        (ReactDOM as any).unstable_batchedUpdates(cb, e);
      }
    : cb;

  if (target?.addEventListener) {
    target.addEventListener(eventType, callback, option);
  }

  return {
    remove: () => {
      if (target?.removeEventListener) {
        target.removeEventListener(eventType, callback, option);
      }
    },
  };
}

export default Handle;
